import React, { FC, useCallback, useEffect, useContext, useRef, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { PrimaryConfirmModal } from '../../../molecules/ModalContainer/PrimaryDialogTemplate/PrimaryConfirmDialog'
import { ModalsContext } from '../../../../contexts'
import { hooks } from '../../../../libs'
import { forceLogout } from '../../../../stores/auth/auth.action'
import { tolNotificationApi, TolOperationsApiError, isTolOperationsNetworkError } from '../../../../libs/api/tolOperations'
import { TolNotification, ValidationError, ValidationErrorResponse } from '../../../../types/tolOperations'
import { NotificationListPage } from './NotificationListPage'
import { NotificationFormPage } from './NotificationFormPage'

export const NotificationManagement: FC = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { accessToken } = hooks.useAuth()
  const { hasNotificationPermission } = hooks.useTolOperation()
  const { appendModal, closeAllModals } = useContext(ModalsContext)

  // refを使う理由: モーダルは毎レンダーで再生成されるため、直接依存に入れると
  // エラーモーダル表示→再レンダー→再fetchの無限ループになる。refで安定化して防ぐ。
  const appendModalRef = useRef(appendModal)
  const closeAllModalsRef = useRef(closeAllModals)

  useEffect(() => {
    appendModalRef.current = appendModal
    closeAllModalsRef.current = closeAllModals
  }, [appendModal, closeAllModals])

  const [notifications, setNotifications] = useState<TolNotification[]>([])
  const [serverErrors, setServerErrors] = useState<ValidationError[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const showFetchErrorModal = useCallback(() => {
    appendModalRef.current(
      <PrimaryConfirmModal
        header="お知らせ一覧の取得に失敗しました。"
        content="再読み込みしてください。"
        label="再読み込み"
        skipButton={false}
        onSubmit={() => {
          window.location.reload()
        }}
      />,
    )
  }, [])

  const showSaveErrorModal = useCallback(() => {
    appendModalRef.current(
      <PrimaryConfirmModal
        header="お知らせの保存に失敗しました。"
        content="時間をおいて再度お試しください。"
        label="閉じる"
        skipButton={false}
        onSubmit={() => {
          closeAllModalsRef.current()
        }}
      />,
    )
  }, [])

  const fetchNotifications = useCallback(async () => {
    if (!accessToken) return

    try {
      setIsLoading(true)
      const data = await tolNotificationApi.getList(accessToken)
      setNotifications(data.information || [])
    } catch (error) {
      if (isTolOperationsNetworkError(error)) {
        dispatch(forceLogout(true))
        return
      }
      const apiError = error as TolOperationsApiError
      // 404はお知らせ未登録（初回）のため空一覧として扱う
      if (apiError.response?.status === 404) {
        setNotifications([])
      } else {
        showFetchErrorModal()
      }
    } finally {
      setIsLoading(false)
    }
  }, [accessToken, dispatch, showFetchErrorModal])

  useEffect(() => {
    if (!hasNotificationPermission) return
    fetchNotifications()
  }, [hasNotificationPermission, fetchNotifications])

  // 保存成功後に一覧画面へ戻った際、state.refresh を検知して一覧を再取得する
  useEffect(() => {
    if (!hasNotificationPermission) return

    const state = location.state as { refresh?: boolean } | undefined
    if (!state?.refresh) return

    fetchNotifications()
    history.replace({ pathname: location.pathname, state: {} })
  }, [hasNotificationPermission, location, history, fetchNotifications])

  const clearServerErrors = useCallback(() => {
    setServerErrors(prev => (prev.length === 0 ? prev : []))
  }, [])

  if (!hasNotificationPermission) {
    return <Redirect to="/unauthorized" />
  }

  const handleSave = async (items: TolNotification[]) => {
    if (!accessToken) return

    setIsSaving(true)
    setServerErrors([])
    try {
      await tolNotificationApi.save(accessToken, { information: items })
    } catch (error) {
      if (isTolOperationsNetworkError(error)) {
        dispatch(forceLogout(true))
        return
      }
      const apiError = error as TolOperationsApiError
      const data = apiError.response?.data as ValidationErrorResponse | undefined
      if (apiError.response?.status === 400 && data?.errors) {
        setServerErrors(data.errors)
      } else {
        showSaveErrorModal()
      }
      return
    } finally {
      setIsSaving(false)
    }
    history.push('/tol-operations/notifications', { refresh: true })
  }

  const handleCancel = () => {
    setServerErrors([])
    history.push('/tol-operations/notifications')
  }

  return (
    <Switch>
      <Route exact path="/tol-operations/notifications">
        <NotificationListPage
          notifications={notifications}
          isLoading={isLoading}
          onOpenEdit={() => history.push('/tol-operations/notifications/form')}
        />
      </Route>
      <Route exact path="/tol-operations/notifications/form">
        <NotificationFormPage
          existingNotifications={notifications}
          isLoading={isLoading}
          isSaving={isSaving}
          serverErrors={serverErrors}
          onClearServerErrors={clearServerErrors}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </Route>
      <Redirect to="/tol-operations/notifications" />
    </Switch>
  )
}
